import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  ChartBarIcon,
  UserGroupIcon,
  PlusIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { MoreVertical, Trash2, Eye } from 'lucide-react';
import { AmazonCard, AmazonCardContent, AmazonCardHeader, AmazonCardTitle } from '../components/ui/AmazonCard';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useTalentCardsList } from '../hooks/useTalentCardsList';
import AppLayout from '../components/Layout/AppLayout';
import ProcessingModal from '../components/ProcessingModal';
import UploadModal from '../components/UploadModal';
import { spanTypography } from '../styles/typography';
import TalRevAPI, { handleApiError } from '../services/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { 
    talentCards, 
    totalCount, 
    completedCount, 
    loading, 
    error, 
    refresh 
  } = useTalentCardsList();

  // Get recent talents from talent cards data
  const recentTalents = talentCards.slice(0, 5).map(tc => ({
    id: tc.id,
    employeeName: tc.employeeProfile.name,
    level: tc.employeeProfile.level,
    status: tc.status || 'unknown',
    processedAt: tc.uploadedAt,
    averageScore: tc.status === 'completed' && tc.scoringResults ? 
      Math.round(Object.values(tc.scoringResults).reduce((acc, dim) => acc + dim.score, 0) / 6 * 10) / 10 : 
      null,
  }));

  // Calculate average processing time from completed talent cards
  const calculateAverageProcessingTime = () => {
    const completedTalents = talentCards.filter(tc => tc.status === 'completed' && tc.processingDuration);
    
    if (completedTalents.length === 0) {
      return 'N/A';
    }
    
    // Parse processing duration strings like "2m 30s" to seconds
    const totalSeconds = completedTalents.reduce((total, tc) => {
      const duration = tc.processingDuration || '';
      const minutesMatch = duration.match(/(\d+)m/);
      const secondsMatch = duration.match(/(\d+)s/);
      
      const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
      const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 0;
      
      return total + (minutes * 60) + seconds;
    }, 0);
    
    const averageSeconds = Math.round(totalSeconds / completedTalents.length);
    const avgMinutes = Math.floor(averageSeconds / 60);
    const avgRemainingSeconds = averageSeconds % 60;
    
    if (avgMinutes > 0) {
      return avgRemainingSeconds > 0 ? `${avgMinutes}m ${avgRemainingSeconds}s` : `${avgMinutes}m`;
    } else {
      return `${avgRemainingSeconds}s`;
    }
  };

  const stats = {
    totalTalents: totalCount,
    thisMonth: totalCount, // For now, assume all are from this month
    averageProcessingTime: calculateAverageProcessingTime(),
    completionRate: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
  };

  const handleViewTalent = (talentId: string, status: string) => {
    if (status === 'completed') {
      // Navigate directly to talent page if completed
      navigate(`/talents/${talentId}`);
    } else {
      // Show processing modal if still processing
      setSelectedTalentId(talentId);
      setShowProcessingModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowProcessingModal(false);
    setSelectedTalentId(null);
  };

  const handleDeleteTalent = async (talentId: string) => {
    setIsDeleting(talentId);
    setDeleteError(null);
    
    try {
      await TalRevAPI.deleteTalentCard(talentId);
      refresh(); // Refresh the talent cards list
    } catch (error) {
      const errorMessage = handleApiError(error);
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(null);
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'default';
      case 'error':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const formatStatusText = (status: string) => {
    // Don't truncate short statuses
    const shortStatuses = ['completed', 'failed', 'error', 'pending', 'uploaded', 'calibrating', 'calibration'];
    if (shortStatuses.includes(status.toLowerCase())) {
      return status;
    }

    // For longer statuses, truncate with ellipsis
    const maxLength = 12;
    if (status.length > maxLength) {
      return status.substring(0, maxLength) + '...';
    }
    return status;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AppLayout
      pageTitle="Dashboard"
      breadcrumb="Dashboard"
      onUploadClick={() => setShowUploadModal(true)}
    >
      <div className="container mx-auto max-w-full px-2">
        {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          {error && (
            <Alert variant="destructive" className="mt-2 max-w-md">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <AmazonCard className="p-3 shadow-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--text-secondary)] truncate font-[var(--font-family-primary)]">
                  Total Talents
                </dt>
                <dd className="text-[var(--font-size-2xl)] font-[var(--font-weight-semibold)] text-[var(--text-primary)] font-[var(--font-family-primary)]">
                  {stats.totalTalents}
                </dd>
              </dl>
            </div>
          </div>
        </AmazonCard>

        <AmazonCard className="p-3 shadow-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-8 w-8 text-success-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--text-secondary)] truncate font-[var(--font-family-primary)]">
                  This Month
                </dt>
                <dd className="text-[var(--font-size-2xl)] font-[var(--font-weight-semibold)] text-[var(--text-primary)] font-[var(--font-family-primary)]">
                  {stats.thisMonth}
                </dd>
              </dl>
            </div>
          </div>
        </AmazonCard>

        <AmazonCard className="p-3 shadow-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-warning-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--text-secondary)] truncate font-[var(--font-family-primary)]">
                  Avg Processing Time
                </dt>
                <dd className="text-[var(--font-size-2xl)] font-[var(--font-weight-semibold)] text-[var(--text-primary)] font-[var(--font-family-primary)]">
                  {stats.averageProcessingTime}
                </dd>
              </dl>
            </div>
          </div>
        </AmazonCard>

        <AmazonCard className="p-3 shadow-md">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--text-secondary)] truncate font-[var(--font-family-primary)]">
                  Completion Rate
                </dt>
                <dd className="text-[var(--font-size-2xl)] font-[var(--font-weight-semibold)] text-[var(--text-primary)] font-[var(--font-family-primary)]">
                  {stats.completionRate}%
                </dd>
              </dl>
            </div>
          </div>
        </AmazonCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Talents */}
        <div className="lg:col-span-2">
          <AmazonCard>
            <AmazonCardHeader>
                <AmazonCardTitle>Recent Talents</AmazonCardTitle>
              </AmazonCardHeader>
              <AmazonCardContent>
                {deleteError && (
                  <Alert className="mb-4 border-red-200 bg-red-50">
                    <AlertDescription className="text-red-800">
                      {deleteError}
                    </AlertDescription>
                  </Alert>
                )}
            {loading && totalCount === 0 ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-[var(--text-secondary)] text-[var(--font-size-sm)] mt-3 font-[var(--font-family-primary)]">Loading talents...</p>
              </div>
            ) : recentTalents.length === 0 ? (
              <div className="text-center py-6">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--text-primary)] font-[var(--font-family-primary)]">No talents yet</h3>
                <p className="mt-1 text-[var(--font-size-sm)] text-[var(--text-secondary)] font-[var(--font-family-primary)]">
                  Get started by uploading your first talent card.
                </p>
                <div className="mt-4">
                  <Button
                    size="sm"
                    className="flex items-center space-x-2 mx-auto"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span className={spanTypography.buttonSpan.primary}>Upload Talent Card</span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {recentTalents.map((talent) => (
                  <div
                    key={talent.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => handleViewTalent(talent.id, talent.status)}
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[var(--font-size-sm)] font-[var(--font-weight-semibold)] text-primary-600 font-[var(--font-family-primary)]">
                          {talent.employeeName.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-[var(--font-weight-medium)] text-[var(--text-primary)] font-[var(--font-family-primary)] truncate">
                          {talent.employeeName}
                        </p>
                        <p className="text-[var(--font-size-sm)] text-[var(--text-secondary)] font-[var(--font-family-primary)] truncate">
                          {talent.level} • {formatDate(talent.processedAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {talent.averageScore && (
                        <span className="hidden sm:inline text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--text-secondary)] font-[var(--font-family-primary)]">
                          {talent.averageScore}/5
                        </span>
                      )}
                      <Badge variant={getStatusVariant(talent.status || 'unknown')} className="whitespace-nowrap">
                        {formatStatusText(talent.status || 'unknown')}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            disabled={isDeleting === talent.id}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewTalent(talent.id, talent.status)}>
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteTalent(talent.id)}
                            className="text-red-600 focus:text-red-600"
                            disabled={isDeleting === talent.id}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {isDeleting === talent.id ? 'Deleting...' : 'Delete'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {recentTalents.length > 0 && (
              <div className="mt-4">
                <Link to="/talents">
                  <Button variant="secondary" className="w-full text-white">
                    View All Talents
                  </Button>
                </Link>
              </div>
            )}
            </AmazonCardContent>
          </AmazonCard>
        </div>

        {/* Quick Actions */}
        <div>
          <AmazonCard>
            <AmazonCardHeader>
              <AmazonCardTitle>Quick Actions</AmazonCardTitle>
            </AmazonCardHeader>
            <AmazonCardContent>
            <div className="space-y-4">
              <div
                className="p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors cursor-pointer"
                onClick={() => setShowUploadModal(true)}
              >
                <div className="flex items-center space-x-3">
                  <DocumentTextIcon className="w-8 h-8 text-primary-600" />
                  <div>
                    <p className="font-[var(--font-weight-medium)] text-[var(--text-primary)] font-[var(--font-family-primary)]">Upload Talent Card</p>
                    <p className="text-[var(--font-size-sm)] text-[var(--text-secondary)] font-[var(--font-family-primary)]">Start new talent analysis</p>
                  </div>
                </div>
              </div>


            </div>
            </AmazonCardContent>
          </AmazonCard>

          {/* Help */}
          <AmazonCard className="mt-4">
            <AmazonCardHeader>
              <AmazonCardTitle>Need Help?</AmazonCardTitle>
            </AmazonCardHeader>
            <AmazonCardContent>
            <div className="text-center space-y-3">
              <p className="text-[var(--font-size-sm)] text-[var(--text-secondary)] font-[var(--font-family-primary)]">
                Get started with AI-powered talent analysis
              </p>
              <Button
                variant="default"
                size="sm"
                className="w-full"
                onClick={() => navigate('/about')}
              >
                View Documentation
              </Button>
            </div>
            </AmazonCardContent>
          </AmazonCard>
        </div>
      </div>
      </div>
      
      {/* Processing Modal */}
      <ProcessingModal
        isOpen={showProcessingModal}
        onClose={handleCloseModal}
        sessionId={selectedTalentId || undefined}
      />

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          refresh(); // Refresh the talent cards list after upload
        }}
      />
    </AppLayout>
  );
};

export default Dashboard;